export type VariableIndexScope = 'var' | 'varEx' | 'string'

export type VariableIndexItem = {
  index: number
  order: number
  name: string
  label: string
  searchText: string
}

export const VARIABLE_INDEX_GROUP_SIZE = 25

type VariableNameEntry = {
  Name?: string
}

type ProjectVariableIndexSource = {
  gameVar?: { Data?: VariableNameEntry[] | null } | null
  gameVarEx?: { Data?: VariableNameEntry[] | null } | null
  gameStr?: { Data?: VariableNameEntry[] | null } | null
}

function getScopeEntries(project: ProjectVariableIndexSource | null | undefined, scope: VariableIndexScope) {
  if (!project) return []
  if (scope === 'var') return project.gameVar?.Data ?? []
  if (scope === 'varEx') return project.gameVarEx?.Data ?? []
  return project.gameStr?.Data ?? []
}

function formatOrderLabel(order: number) {
  return String(order).padStart(3, '0')
}

export function buildVariableIndexItems(
  project: ProjectVariableIndexSource | null | undefined,
  scope: VariableIndexScope,
  options?: { allowNegativeOne?: boolean },
): VariableIndexItem[] {
  const allowNegativeOne = options?.allowNegativeOne === true
  const entries = getScopeEntries(project, scope)
  const items: VariableIndexItem[] = []

  if (allowNegativeOne) {
    items.push({
      index: -1,
      order: 0,
      name: '无',
      label: `${formatOrderLabel(0)}: 无`,
      searchText: '0 -1 无 none',
    })
  }

  entries.forEach((entry, index) => {
    const order = index + 1
    const name = String(entry?.Name ?? '')
    const fallbackName = name || '未命名'
    items.push({
      index,
      order,
      name,
      label: `${formatOrderLabel(order)}: ${fallbackName}`,
      searchText: `${order} ${index} ${fallbackName} ${name}`.toLowerCase(),
    })
  })

  return items
}

export function clampVariableIndex(
  value: number,
  project: ProjectVariableIndexSource | null | undefined,
  scope: VariableIndexScope,
  options?: { allowNegativeOne?: boolean },
): number {
  const items = buildVariableIndexItems(project, scope, options)
  if (!items.length) {
    return options?.allowNegativeOne ? -1 : 0
  }

  const min = items[0].index
  const max = items[items.length - 1]?.index ?? min
  if (!Number.isFinite(value)) return min
  if (value < min) return min
  if (value > max) return max
  return value
}

export function getVariableIndexDisplayLabel(
  value: number,
  project: ProjectVariableIndexSource | null | undefined,
  scope: VariableIndexScope,
  options?: { allowNegativeOne?: boolean; placeholder?: string },
): string {
  const items = buildVariableIndexItems(project, scope, options)
  const hit = items.find((item) => item.index === value)
  if (hit) return hit.label
  if (Number.isFinite(value)) return `当前值：${value}`
  return options?.placeholder ?? '请选择变量'
}
